/**
 * DiagnosticResultsForm — shown right under a generated diagnostic.
 * Teacher enters the % of students who picked each option for each
 * question. Submitting kicks off Stage 3 (Adaptive Lesson 1).
 */
import React, { useMemo, useState } from "react";
import { GraduationCap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { LETTERS } from "./utils";

const DiagnosticResultsForm = ({ activity, onSubmit, submitting }) => {
  const questions = useMemo(() => {
    const me = (activity?.inputs || {}).math_engine || {};
    return Array.isArray(me.questions) ? me.questions : [];
  }, [activity]);

  // rows[i] = { A, B, C, D } as strings while editing so the user can
  // clear a cell. Converted to numbers at submit time.
  const [rows, setRows] = useState(() =>
    questions.map(() => ({ A: "", B: "", C: "", D: "" }))
  );

  if (!questions.length) {
    return (
      <div
        className="ae-card p-6 text-sm text-[#4a6984]/85"
        data-testid="math-engine-results-form-missing"
      >
        We couldn't find the diagnostic's question metadata, so the class
        results form can't render. Re-generate the diagnostic and try again.
      </div>
    );
  }

  const setCell = (i, letter, value) => {
    const cleaned = value.replace(/[^\d.]/g, "");
    setRows((prev) => {
      const next = prev.map((r) => ({ ...r }));
      next[i][letter] = cleaned;
      return next;
    });
  };

  const rowTotal = (r) =>
    LETTERS.reduce((s, L) => s + (parseFloat(r[L]) || 0), 0);

  const rowComplete = (r) => {
    const t = rowTotal(r);
    return t >= 95 && t <= 105;
  };

  const allComplete = rows.every(rowComplete);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!allComplete) {
      toast.error("Each row should total roughly 100%.");
      return;
    }
    const payload = rows.map((r) => ({
      A: parseFloat(r.A) || 0,
      B: parseFloat(r.B) || 0,
      C: parseFloat(r.C) || 0,
      D: parseFloat(r.D) || 0,
    }));
    onSubmit(payload);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="ae-card p-6 md:p-8"
      data-testid="math-engine-results-form"
    >
      <div className="flex items-start gap-3 mb-5">
        <span className="h-10 w-10 rounded-xl bg-[#96ded1]/30 grid place-items-center text-[#1c2d3d] shrink-0">
          <GraduationCap className="h-5 w-5" />
        </span>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/70">
            Next step · Enter class results
          </p>
          <h3 className="font-black text-2xl text-[#1c2d3d] tracking-tight">
            What happened when you ran the diagnostic?
          </h3>
          <p className="text-sm text-[#4a6984]/80 mt-1 leading-relaxed max-w-2xl">
            Type the percentage of students who chose each option for each
            question. The engine will identify the dominant misconception and
            draft Lesson 1 with three differentiated paths.
          </p>
        </div>
      </div>

      <div className="overflow-x-auto -mx-2 px-2">
        <table className="w-full text-sm" data-testid="math-engine-results-table">
          <thead>
            <tr className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
              <th className="text-left py-2 pr-3">Question</th>
              {LETTERS.map((L) => (
                <th key={L} className="py-2 px-2 text-center">
                  {L}
                </th>
              ))}
              <th className="py-2 pl-3 text-center">Total</th>
            </tr>
          </thead>
          <tbody>
            {questions.map((q, i) => {
              const correct = String(q.correct || "").toUpperCase();
              const total = rowTotal(rows[i]);
              const ok = rowComplete(rows[i]);
              return (
                <tr
                  key={i}
                  className="border-t border-[#4a6984]/10 align-middle"
                  data-testid={`math-engine-results-row-${i + 1}`}
                >
                  <td className="py-3 pr-3">
                    <p className="font-bold text-[#1c2d3d]">
                      Q{i + 1}{" "}
                      <span className="text-[#4a6984]/60 font-semibold">
                        · {q.expectation_code}
                      </span>
                    </p>
                    <p className="text-[11px] text-[#4a6984]/70 mt-0.5">
                      Correct option:{" "}
                      <span className="font-bold">{correct}</span>
                    </p>
                  </td>
                  {LETTERS.map((L) => (
                    <td key={L} className="py-2 px-1">
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={rows[i][L]}
                          onChange={(e) => setCell(i, L, e.target.value)}
                          placeholder="0"
                          data-testid={`math-engine-results-${i + 1}-${L}`}
                          className={`w-full text-center rounded-lg border px-2 py-1.5 text-sm font-semibold tracking-tight focus:outline-none focus:ring-2 focus:ring-[#96ded1] ${
                            L === correct
                              ? "border-[#96ded1] bg-[#96ded1]/10 text-[#1c2d3d]"
                              : "border-[#4a6984]/20 bg-white text-[#1c2d3d]"
                          }`}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-[#4a6984]/50 pointer-events-none">
                          %
                        </span>
                      </div>
                    </td>
                  ))}
                  <td className="py-2 pl-3 text-center">
                    <span
                      className={`text-xs font-bold ${
                        ok
                          ? "text-[#1c2d3d]"
                          : total > 0
                          ? "text-amber-700"
                          : "text-[#4a6984]/50"
                      }`}
                      data-testid={`math-engine-results-row-total-${i + 1}`}
                    >
                      {total ? `${Math.round(total)}%` : "—"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-[11px] text-[#4a6984]/70 leading-relaxed">
        Each row should total ~100% (95-105% is fine). Don't sweat the
        decimals — the engine normalises before reading.
      </p>

      <div className="mt-5 flex flex-wrap gap-2 justify-end">
        <Button
          type="submit"
          disabled={!allComplete || submitting}
          data-testid="math-engine-results-submit"
          className="bg-[#4a6984] hover:bg-[#1c2d3d] text-white disabled:opacity-50"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Designing Lesson 1…
            </>
          ) : (
            <>
              <GraduationCap className="h-4 w-4 mr-2" /> Generate Adaptive Lesson 1
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default DiagnosticResultsForm;
